// Styles
import styles from './FilterSelect.module.css';

// Components
import Picto from '../../atoms/Picto/Picto';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';

/**
 * @typedef {object} FilterSelectOption
 * @property {string | number} value - Option value
 * @property {string} label - Option display label
 */

/**
 * FilterSelect component - Generic select with prev/next navigation buttons
 * @param {object} props
 * @param {FilterSelectOption[]} props.options - Array of options for the select
 * @param {string | number | null} props.value - Current selected value
 * @param {(value: string | number) => void} props.handleValue - Function to handle select value change
 * @param {(direction: 'minus' | 'plus') => void} [props.handlePrevNextValue] - Function to handle prev/next navigation
 * @param {string} [props.placeholder] - Placeholder text when no value
 * @param {string} [props.ariaLabel] - Aria label for accessibility
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Whether the component is disabled
 * @param {boolean} [props.hasPrevNext] - Whether the component has prev/next buttons
 * @param {(option: FilterSelectOption) => React.ReactNode} [props.children] - Children function to render options
 * @param {ApiPageTemplate} [props.template] - Template of the component
 * @param {boolean} [props.isLoading] - Whether the component is loading
 * @returns {JSX.Element} FilterSelect component
 */
function FilterSelect({
  options,
  value,
  handleValue,
  handlePrevNextValue = () => {},
  placeholder = 'Select...',
  ariaLabel = 'Select option',
  className = '',
  disabled = false,
  hasPrevNext = true,
  children,
  template = 'table',
  isLoading = false,
}) {
  /**
   * Handle select change
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const handleSelectChange = (e) => {
    handleValue(e.target.value);
  };

  /**
   * Handle previous button click
   */
  const handlePrevious = () => {
    handlePrevNextValue('minus');
  };

  /**
   * Handle next button click
   */
  const handleNext = () => {
    handlePrevNextValue('plus');
  };

  const hasValue = value !== null && value !== undefined && value !== '';
  const displayValue = hasValue ? String(value) : '';

  if (isLoading) {
    return (
      <Skeleton
        theme="table"
        shape="rectangle"
        width="200px"
        height="28px"
        className={`${styles.wrapper} container ${styles[template]} ${className}`}
      />
    );
  }

  return (
    <div className={`${styles.wrapper} container ${styles[template]} ${className}`}>
      {hasPrevNext ? (
        <button
          aria-label="Previous year"
          type="button"
          onClick={handlePrevious}
          className={`${styles.button} ${styles['button-left']} minus`}
          disabled={disabled}
        >
          <Picto icon="chevron-left" className={styles['button-icon']} />
        </button>
      ) : null}

      <div className={`${styles.group} ${disabled ? styles['group-disabled'] : ''}`}>
        {hasValue ? (
          <select
            name="group"
            value={displayValue}
            onChange={handleSelectChange}
            className={styles['group-select']}
            aria-label={ariaLabel}
            disabled={disabled}
          >
            {options.map((option) => (
              children(option)
            ))}
          </select>
        ) : (
          <span className={styles['group-placeholder']}>{placeholder}</span>
        )}
        <Picto icon="unfold-more" className={styles['group-icon']} />
      </div>

      {hasPrevNext ? (
        <button
          aria-label="Next year"
          type="button"
          onClick={handleNext}
          className={`${styles.button} ${styles['button-right']} plus`}
          disabled={disabled}
        >
          <Picto icon="chevron-right" className={styles['button-icon']} />
        </button>
      ) : null}
    </div>
  );
}

export default FilterSelect;
